// src/features/user-portfolios/components/PortfolioPerformanceChart.tsx
//
// Advanced portfolio-vs-benchmark performance chart. The backend returns the
// raw cumulative TWR curve + the aligned benchmark close for every day since
// inception. This component "rebases" both series client-side so the START of
// whatever timeframe the user selects equals exactly 0% — giving instant
// timeframe toggles (7D / 1M / 3M / 6M / YTD / 1Y / MAX) without a re-fetch.

import { useMemo } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { ChartDataPoint } from '../api';
import {
    PERIODS,
    computeRebasedPerformance,
    type Period,
} from '../utils/performance';

interface Props {
    data: ChartDataPoint[];
    benchmarkLabel: string;
    period: Period;
    onPeriodChange: (p: Period) => void;
}

export const PortfolioPerformanceChart = ({
    data,
    benchmarkLabel,
    period,
    onPeriodChange,
}: Props) => {
    // Rebase both series so the selected timeframe starts at exactly 0%. The
    // baseline convention (value ON/BEFORE the cutoff, windows anchored to the
    // last available server date) lives in the shared util so the "Return vs …"
    // KPI card always reports the SAME period performance as this curve.
    const chartData = useMemo(
        () => computeRebasedPerformance(data, period).chartData,
        [data, period],
    );

    return (
        <div className="mb-8 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-5 shadow-sm">
            <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
                    Performance vs Benchmark
                </h3>

                {/* Timeframe Toggles */}
                <div className="flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800">
                    {PERIODS.map((p) => (
                        <button
                            key={p}
                            onClick={() => onPeriodChange(p)}
                            className={`rounded-md px-3 py-1 text-xs font-semibold transition-all ${
                                period === p
                                    ? 'bg-white text-blue-600 shadow-sm dark:bg-slate-600 dark:text-white'
                                    : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-300'
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
            </div>

            <div className="h-80 w-full">
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="colorPort" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#334155"
                                opacity={0.2}
                            />
                            <XAxis
                                dataKey="formattedDate"
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                                minTickGap={30}
                            />
                            <YAxis
                                tickFormatter={(val) => `${val.toFixed(0)}%`}
                                tick={{ fontSize: 11, fill: '#64748b' }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#0f172a',
                                    borderColor: '#334155',
                                    borderRadius: '8px',
                                }}
                                itemStyle={{ fontWeight: 'bold' }}
                                labelStyle={{ color: '#94a3b8', marginBottom: '4px' }}
                                formatter={(value: number, name: string) => [
                                    `${value > 0 ? '+' : ''}${value.toFixed(2)}%`,
                                    name === 'Benchmark' ? benchmarkLabel : name,
                                ]}
                            />
                            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                            <Area
                                type="monotone"
                                dataKey="Benchmark"
                                stroke="#94a3b8"
                                strokeWidth={2}
                                strokeDasharray="4 4"
                                fill="none"
                                activeDot={{ r: 4 }}
                            />
                            <Area
                                type="monotone"
                                dataKey="Portfolio"
                                stroke="#3b82f6"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorPort)"
                                activeDot={{ r: 6, strokeWidth: 0 }}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex h-full items-center justify-center text-sm text-slate-400">
                        Not enough data points.
                    </div>
                )}
            </div>
        </div>
    );
};

export default PortfolioPerformanceChart;
